"use client"

import { useState } from "react"
import { X, Mail, UserPlus } from "lucide-react"
import axios from "axios"
import config from "../config"

const AddGuestModal = ({ isOpen, onClose, event, fetchInvitationsAndGuests, showSuccessModal, showFailureModal }) => {
  const [emailChips, setEmailChips] = useState([])
  const [inputValue, setInputValue] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return re.test(email)
  }

  const handleAddGuest = async (e) => {
    e.preventDefault()

    const token = localStorage.getItem("access_token")
    if (!token) {
      showFailureModal("Authentication token missing. Please log in.")
      return
    }

    setIsSubmitting(true)

    try {
      // Process all emails from both chips and input field
      const emailList = [...emailChips, ...(inputValue ? [inputValue] : [])]
        .map(email => email.trim())
        .filter(email => email && validateEmail(email))

      if (emailList.length === 0) {
        showFailureModal("Please enter at least one valid email.")
        return
      }

      // Check for existing invitations
      const existingInvitations = await axios.get(`${config.API_BASE_URL}/invitations/`, {
        params: { event: event.id },
        headers: { Authorization: `Bearer ${token}` },
      })

      // Extract email addresses from existing invitations
      const existingEmails = new Set(
        existingInvitations.data?.results?.map(inv =>
          (inv.recipient_email || '').toLowerCase()
        ) || []
      )

      // Filter out emails that already have invitations
      const newEmails = emailList.filter(email =>
        !existingEmails.has(email.toLowerCase())
      )

      if (newEmails.length === 0) {
        showFailureModal("All entered emails already have invitations for this event.")
        return
      }

      // Send invitations for new emails
      const invitationPromises = newEmails.map(async (email) => {
        try {
          const response = await axios.post(
            `${config.API_BASE_URL}/invitations/invite/`,
            {
              status: "pending",
              event: event.id,
              recipient_email: email,
              initiated_by: "creator",
              message: `You have been invited to the event '${event.title}'`
            },
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          )
          return { success: true, email, response }
        } catch (error) {
          // console.error(`Error sending invitation to ${email}:`, error)
          return {
            success: false,
            email,
            error: error.response?.data || error.message
          }
        }
      })

      // Wait for all invitations to be processed
      const results = await Promise.all(invitationPromises)
      const successfulInvites = results.filter(r => r.success)
      const failedInvites = results.filter(r => !r.success)

      if (successfulInvites.length > 0) {
        showSuccessModal(
          `Successfully sent ${successfulInvites.length} invitation(s).` +
          (failedInvites.length > 0
            ? ` Failed to send ${failedInvites.length} invitation(s).`
            : '')
        )

        // Refresh the guest list and reset form
        await fetchInvitationsAndGuests()
        setInputValue("")
        setEmailChips([])

        // Close the modal if all invitations were successful
        if (failedInvites.length === 0) {
          onClose()
        }
      } else {
        showFailureModal("Failed to send any invitations. Please try again.")
      }
    } catch (error) {
      // console.error("Error in handleAddGuest:", error)
      showFailureModal(
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "An error occurred while sending invitations. Please try again."
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <UserPlus className="text-blue-600" size={20} />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">Add Guests</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-150"
            disabled={isSubmitting}
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleAddGuest} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Addresses
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <div className="relative">
                <div className="flex flex-wrap gap-2 p-3 border border-gray-300 rounded-lg bg-white">
                  {emailChips.map((email, index) => (
                    <div
                      key={index}
                      className="bg-blue-100 text-blue-800 rounded-lg text-sm flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-blue-200 transition-colors duration-200"
                      onClick={() => {
                        if (isSubmitting) return
                        const newChips = emailChips.filter((_, i) => i !== index)
                        setEmailChips(newChips)
                      }}
                    >
                      {email}
                      <button
                        type="button"
                        className="ml-2 p-1 text-blue-800 hover:text-blue-600"
                        onClick={(e) => {
                          e.stopPropagation()
                          if (isSubmitting) return
                          const newChips = emailChips.filter((_, i) => i !== index)
                          setEmailChips(newChips)
                        }}
                        disabled={isSubmitting}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => {
                      if ((e.key === ',' || e.key === 'Enter') && inputValue) {
                        e.preventDefault()
                        const trimmed = inputValue.trim()
                        if (trimmed && validateEmail(trimmed) && !emailChips.includes(trimmed)) {
                          setEmailChips([...emailChips, trimmed])
                          setInputValue("")
                        } else if (!validateEmail(trimmed)) {
                          showFailureModal("Please enter a valid email address.")
                        }
                      }
                    }}
                    className="flex-1 bg-transparent focus:outline-none focus:ring-0 placeholder-gray-400 min-w-[100px]"
                    placeholder={emailChips.length === 0 ? "Type email addresses..." : ""}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {emailChips.length > 0
                ? "Press Enter or add a comma to add another email"
                : "Separate multiple emails with commas"}
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 font-medium rounded-lg transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || (emailChips.length === 0 && !inputValue.trim())}
              className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Sending..." : "Send Invitations"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddGuestModal
